import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Newspaper, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['release', 'regulation', 'feature', 'partnership', 'maintenance'];

export default function AdminNews() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', category: 'update', published: true });

  const fetchNews = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/news`);
      setNews(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load news');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchNews(); }, []);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/news/${editing.id}`, form);
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/news`, form);
        toast.success('Article published');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ title: '', content: '', category: 'update', published: true });
      await fetchNews();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/news/${id}`);
      toast.success('Article deleted');
      await fetchNews();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete article');
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ title: item.title, content: item.content, category: item.category, published: item.published });
    setDialogOpen(true);
  };

  return (
    <div data-testid="admin-news-page" className="space-y-6">
      <div className="cp-page-header">
        <h1 className="cp-page-title flex items-center gap-2">
          <Newspaper className="w-6 h-6 text-accent" /> Manage News
        </h1>
        <Button onClick={() => { setEditing(null); setForm({ title: '', content: '', category: 'update', published: true }); setDialogOpen(true); }}
          className="bg-primary hover:bg-primary/90" data-testid="add-news-btn">
          <Plus className="w-4 h-4 mr-2" /> New Article
        </Button>
      </div>

      <div className="cp-table-wrapper animate-fade-in">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => <TableRow key={`sk-${i}`}>{[...Array(5)].map((__, j) => <TableCell key={`sk-${i}-${j}`}><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>)}</TableRow>)
            ) : news.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                <Newspaper className="w-10 h-10 mx-auto mb-2 opacity-30" /> No articles yet
              </TableCell></TableRow>
            ) : news.map(item => (
              <TableRow key={item.id} data-testid={`admin-news-row-${item.id}`}>
                <TableCell className="font-medium max-w-xs truncate">{item.title}</TableCell>
                <TableCell><span className="cp-badge-indigo text-xs capitalize">{item.category}</span></TableCell>
                <TableCell>
                  {item.published
                    ? <span className="cp-badge-green flex items-center gap-1 w-fit"><Eye className="w-3 h-3" />Published</span>
                    : <span className="cp-badge-amber flex items-center gap-1 w-fit"><EyeOff className="w-3 h-3" />Draft</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{new Date(item.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`edit-news-${item.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-destructive hover:text-destructive" data-testid={`delete-news-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="news-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Article' : 'New Article'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Article title..." data-testid="news-title-input" /></div>
            <div><Label>Content</Label><Textarea rows={5} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Article content..." data-testid="news-content-input" /></div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="news-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Published</Label>
              <Switch checked={form.published} onCheckedChange={v => setForm({ ...form, published: v })} data-testid="news-published-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="news-cancel-btn">Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="news-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? 'Update' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
